import { useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import { useAppStore } from '@/store'

interface RenameHandlers {
  /**
   * The current name, used to revert the optimistic update if the PATCH fails.
   * Omit when the caller doesn't render an optimistic value.
   */
  previousName?: string
  /**
   * Applies a name to local state. Called once up-front with the new name
   * (optimistic) and again with `previousName` if the request fails.
   */
  applyName?: (name: string) => void
  /**
   * Id to key the board's `renamedDocNames` overlay by — the id the on-canvas
   * card is keyed under. Defaults to `docId`. Differs only for P&ID/PFD graph
   * cards, where the card id is the graph doc but the renamed document is its
   * source (so the API/documents-store id is the source while the overlay must
   * be keyed by the graph card id the board renders).
   */
  overlayKey?: string
}

/**
 * Shared document-rename gesture used by every filename surface (file-preview
 * headers, document viewers, data-catalog previews). Mirrors the board-card
 * rename flow in `DocumentPreviewShape`:
 *
 *  1. optimistic local update (`applyName`),
 *  2. optimistic update of the shared board store — `updateDocumentName` (the
 *     `documents` array the board derives card labels from) and
 *     `setRenamedDocName` (the `renamedDocNames` overlay BoardCanvas reads) —
 *     so a rename done in a viewer/preview shows up on the board (and any other
 *     surface) immediately, instead of only after a full reload re-fetches from
 *     the DB,
 *  3. `PATCH /v1/document/:id/rename`, sonner toast on success,
 *  4. on failure: revert local + store and show an error toast.
 *
 * A per-hook in-flight guard prevents a blur-then-Enter double submit from
 * firing two requests. Returns a stable `rename(docId, newName, handlers?)`
 * callback that resolves `true` when the rename persisted, else `false`.
 */
export function useRenameDocument() {
  const { t } = useTranslation('board')
  const updateDocumentName = useAppStore((s) => s.updateDocumentName)
  const setRenamedDocName = useAppStore((s) => s.setRenamedDocName)
  const inFlightRef = useRef(false)

  return useCallback(
    async (
      docId: string | undefined,
      newName: string,
      handlers?: RenameHandlers,
    ): Promise<boolean> => {
      if (!docId || inFlightRef.current) return false
      inFlightRef.current = true

      const overlayKey = handlers?.overlayKey ?? docId

      // Capture the name to restore on failure. The store update is applied
      // unconditionally below, so its revert can't depend on the caller passing
      // `previousName` — fall back to whatever the store currently holds for this
      // doc (overlay first, then the documents array). Without this, a rejected
      // rename from a caller that omitted `previousName` would leave the board
      // store showing a name the server refused.
      const store = useAppStore.getState()
      const revertName =
        handlers?.previousName ??
        store.renamedDocNames[overlayKey] ??
        store.documents.find((d) => d.id === docId)?.name

      // Optimistic: repaint with the new name before the round-trip — local
      // state for this surface, plus the shared board store so the on-canvas
      // card / lists / other previews update too.
      handlers?.applyName?.(newName)
      setRenamedDocName(overlayKey, newName)
      updateDocumentName(docId, newName)
      try {
        await apiService.renameDocument(docId, newName)
        toast.success(t('board:toasts.document_renamed', { defaultValue: 'Document renamed' }))
        return true
      } catch {
        // Revert local + store so the UI doesn't keep a name the server rejected.
        if (handlers?.previousName !== undefined) handlers.applyName?.(handlers.previousName)
        if (revertName !== undefined) {
          setRenamedDocName(overlayKey, revertName)
          updateDocumentName(docId, revertName)
        }
        toast.error(
          t('board:toasts.failed_to_rename_document', { defaultValue: 'Failed to rename document' }),
        )
        return false
      } finally {
        inFlightRef.current = false
      }
    },
    [t, updateDocumentName, setRenamedDocName],
  )
}

export default useRenameDocument
